import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for highlighting new answers in a live-updating answer list.
 * Handles scroll notification, highlight/fade animation, and session persistence.
 */
export default class extends Controller {
  static targets = ["row"]
  static values = {
    questionId: String
  }

  /**
   * Called when the controller is connected to the DOM.
   * Sets up notification, scroll listener, and highlights new answers.
   */
  connect() {
    const storageKey = this.getStorageKey()
    const previousAnswers = sessionStorage.getItem(storageKey)
    this.previousAnswers = new Set(previousAnswers ? JSON.parse(previousAnswers) : [])

    // Find notification element and set up click handler
    this.notification = document.querySelector('.new-answers-highlight')
    if (this.notification) {
      // Ensure it starts hidden
      this.notification.classList.add('d-none')
      this.notification.addEventListener('click', () => {
        const lastAnswer = this.element.querySelector('.answer-row:last-child')
        lastAnswer?.scrollIntoView({
          behavior: 'smooth',
          block: 'center' // Center the element below the navbar
        })
        this.hideNotification()
      })
    }

    // Set up scroll listener for notification visibility
    this.scrollHandler = this.checkNewAnswersVisibility.bind(this)
    window.addEventListener('scroll', this.scrollHandler, { passive: true })

    this.handleNewAnswers()
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up listeners and saves state.
   */
  disconnect() {
    window.removeEventListener('scroll', this.scrollHandler)
    // Save state before controller is torn down
    const storageKey = this.getStorageKey()
    sessionStorage.setItem(storageKey, JSON.stringify(Array.from(this.previousAnswers)))
  }

  /**
   * Returns a unique sessionStorage key for the current question.
   * @returns {string}
   */
  getStorageKey() {
    // Get question ID from the closest parent with data-question-id
    const questionId = this.element.closest('[data-question-id]')?.dataset.questionId
    return `previousAnswers-${questionId || 'default'}`
  }

  /**
   * Handles detection and highlighting of new answers.
   * Also manages notification and fade timers.
   */
  handleNewAnswers() {
    const currentAnswers = this.getAnswerIds()
    const newAnswers = currentAnswers.filter(id => !this.previousAnswers.has(id))

    if (newAnswers.length > 0) {
      this.highlightNewAnswers(newAnswers)
      this.newAnswerIds = newAnswers

      // If new answers are out of view, show notification; otherwise, start fade timers
      const lastAnswer = this.element.querySelector('.answer-row:last-child')
      if (!this.isElementInViewport(lastAnswer)) {
        this.showScrollNotification()
      } else {
        this.startFadeTimers(newAnswers)
      }

      // Update stored state with new answers
      newAnswers.forEach(id => this.previousAnswers.add(id))
      const storageKey = this.getStorageKey()
      sessionStorage.setItem(storageKey, JSON.stringify(Array.from(this.previousAnswers)))
    }
  }

  /**
   * Returns an array of all answer IDs currently in the DOM.
   * @returns {Array<string>}
   */
  getAnswerIds() {
    return Array.from(this.element.querySelectorAll('.answer-row'))
      .map(row => row.dataset.answerId)
  }

  /**
   * Adds highlight class to new answer rows.
   * @param {Array<string>} newAnswerIds
   */
  highlightNewAnswers(newAnswerIds) {
    newAnswerIds.forEach(id => {
      const row = this.element.querySelector(`[data-answer-id="${id}"]`)
      if (row) {
        row.classList.add('highlight-new')
      }
    })
  }

  /**
   * Starts fade-out timers for highlighted answer rows.
   * @param {Array<string>} answerIds
   */
  startFadeTimers(answerIds) {
    answerIds.forEach(id => {
      const row = this.element.querySelector(`[data-answer-id="${id}"]`)
      if (row) {
        // Start fade out after a delay
        setTimeout(() => {
          row.classList.add('highlight-fade-out')
          row.classList.remove('highlight-new')

          // Remove classes after animation completes
          setTimeout(() => {
            row.classList.remove('highlight-fade-out')
          }, 5000)
        }, 4000) // Keep solid highlight for 4 seconds before starting fade
      }
    })
  }

  /**
   * Checks if an element is fully in the viewport, accounting for navbar and buffer.
   * @param {Element} el
   * @returns {boolean}
   */
  isElementInViewport(el) {
    if (!el) return false
    const rect = el.getBoundingClientRect()
    const navbarHeight = 62 // Height of the sticky navbar
    const buffer = 20 // Additional buffer to ensure element is comfortably in view
    return (
      rect.top >= navbarHeight + buffer &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  /**
   * Checks if any new answers are now visible and hides notification if so.
   * Starts fade timers when answers come into view.
   */
  checkNewAnswersVisibility() {
    if (!this.newAnswerIds || !this.notification) return

    // Check if any of the new answers are now visible
    const anyNewAnswerVisible = this.newAnswerIds.some(id => {
      const row = this.element.querySelector(`[data-answer-id="${id}"]`)
      return row && this.isElementInViewport(row)
    })

    if (anyNewAnswerVisible) {
      this.hideNotification()
      // Start fade timers when answers come into view
      this.startFadeTimers(this.newAnswerIds)
      this.newAnswerIds = null
    }
  }

  /**
   * Shows the scroll notification for new answers.
   */
  showScrollNotification() {
    if (this.notification) {
      this.notification.classList.remove('d-none')
    }
  }

  /**
   * Hides the scroll notification for new answers.
   */
  hideNotification() {
    if (this.notification) {
      this.notification.classList.add('d-none')
    }
  }
}
